import * as React from "react";
import { styled, Theme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import Header from "../Component/Header";
import PageRoutes from "../../utils/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StickyFooterShell } from "../Component/Footer/StickyFooterShell";

/**
 * The signed-out chrome, and the pass-through for v2 pages that bring their own
 * layout.
 *
 * Like MainContainer, this used to hold a second layout chosen by config.v2.nav
 * — a 240px MUI permanent drawer with its own link list, its own support and
 * legal entries, and a non-sticky footer. Every tenant is on the nav in the app
 * bar now, so the drawer and everything only it rendered went with the flag.
 */

const drawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }: { theme: Theme; open?: boolean }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

export default function MainContainer({
  children,
  useV2Layout = false,
  hideFooter = false,
}: {
  children: React.ReactElement;
  useV2Layout?: boolean;
  hideFooter?: boolean;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const game_id = searchParams.get("game_id");

  const handleNavigate = (game_id: any) => {
    navigate(PageRoutes.Watch.replace(":gameId", game_id));
  };

  React.useEffect(() => {
    if (!useV2Layout && game_id) {
      handleNavigate(game_id);
    }
  });

  // v2 pages render a bare shell (they bring their own layout). Returning here,
  // after every hook above, keeps hook order stable across renders (rules of hooks).
  if (useV2Layout) {
    return <>{children}</>;
  }

  return (
    <Box>
      <CssBaseline />
      <AppBar
        sx={{
          backgroundColor: "#edf2f7",
          boxShadow: "none",
        }}
        position="fixed"
      >
        <Toolbar disableGutters sx={{ px: "0px !important" }}>
          <Box sx={{ width: 1 }}>
            <Header />
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: 0,
          ml: 0,
          position: "fixed",
          top: "4rem",
          left: 0,
          right: 0,
          bottom: 0,
          overflowY: "auto",
        }}
      >
        <StickyFooterShell hideFooter={hideFooter}>
          {children}
        </StickyFooterShell>
      </Box>
    </Box>
  );
}
